// Modules
import styled from "styled-components"
import ArrowLeft from "@mui/icons-material/ArrowLeft"
import ArrowRight from "@mui/icons-material/ArrowRight"
import { useState, useEffect } from "react"

// Constants
import colors from "~constants/colors"

// Shapes
import { TablePaginatorShape } from "~shapes/api-table"

export interface TablePaginatorProps {
  page: TablePaginatorShape
  onPageChange: (_id: number) => void
}

const TablePaginator = (props: TablePaginatorProps) => {
  // Set component state
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [pagesAvailable, setPagesAvailable] = useState<boolean[]>([])

  // Component constants
  const { page, onPageChange } = props

  // useEffect
  useEffect(() => {
    setPagesAvailable(Array(page.totalPages).fill(null))
  }, [page])

  // Method to handle the pagination
  const moveToStep = (id: number) => {
    onPageChange(id)
    setCurrentPage(id)
  }

  // Method to move to next step
  const moveToNextStep = () => {
    if (page.number !== page.totalPages - 1) {
      moveToStep(page.number + 1)
    }
  }

  // Method to move to prev step
  const moveToPrevStep = () => {
    if (page.number !== 0) {
      moveToStep(page.number - 1)
    }
  }

  return (
    <PaginatorContainer>
      <ButtonContainer>
        <BaseButton
          style={{ marginRight: "10px" }}
          onClick={moveToPrevStep}
          disabled={currentPage === 0}
        >
          <ArrowLeft />
        </BaseButton>
        Prev
      </ButtonContainer>

      <PagesLinksContainer>
        {pagesAvailable.map((_el, i) => (
          <li onClick={() => moveToStep(i)} key={i}>
            <PageButton className={i === currentPage ? "active" : ""}>
              {i + 1}
            </PageButton>
          </li>
        ))}
      </PagesLinksContainer>

      <ButtonContainer>
        Next
        <BaseButton
          style={{ marginLeft: "10px" }}
          onClick={moveToNextStep}
          disabled={currentPage === page.totalPages - 1}
        >
          <ArrowRight />
        </BaseButton>
      </ButtonContainer>
    </PaginatorContainer>
  )
}

export default TablePaginator

const BaseButton = styled.button`
  background-color: ${colors.blue1};
  border-radius: 6px;
  border: none;
  color: ${colors.caribbean};
  cursor: pointer;
  height: 40px;
  outline: none;
  padding: 0;
  width: 40px;

  &[disabled] {
    cursor: not-allowed;
    opacity: 0.5;
  }

  > svg {
    font-size: 40px;
  }
`

const PageButton = styled(BaseButton)`
  background-color: transparent;
  color: inherit;
  font-weight: bold;
  height: 32px;
  width: 32px;

  &.active {
    background-color: ${colors.blue1};
    color: ${colors.caribbean};
  }
`

const ButtonContainer = styled.span`
  align-items: center;
  display: inline-flex;
`

const PaginatorContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`

const PagesLinksContainer = styled.ul`
  display: flex;
  list-style-type: none;
  margin: 0;
  padding: 0;

  > li {
    margin: 0;

    &:not(:last-of-type) {
      margin-right: 10px;
    }
  }
`
